import { z } from 'zod';
import { createToolSignature } from './tool-signature';

export const displayConversationToolSignature = createToolSignature({
    name: 'displayConversation',
    description: `Display a selection of conversations/chats to the user from prospectIds. This tool presents the user with a list of chats where the user will see their Linkedin/Email chat with the contact with a pre-suggested message (action) they can send. This display tool also works for showing chats that don't have any previous conversation history. The tool is useful to both show the conversation as well as for suggesting messages to send to the contact. This UI will display all profile information about the contact, the conversation history the suggested message (action) and a motivation for the action. This tool should be used at the very end of your response to display the final result to the user.`,
    inputSchema: z.object({
        prospectIds: z
            .array(z.string())
            .describe(
                'The prospectIds of the conversations to display. ProspectIds are associated with a contact (but different from the userContactId), prospectIds does not contain any hyphens and is not on the form of a uuid v4',
            ),
        actions: z
            .array(
                z.object({
                    prospectId: z
                        .string()
                        .describe(
                            'The prospectId of the conversation (from the prospectIds input array) that the action is associated with',
                        ),
                    fullName: z.string().describe('The full name of the contact'),
                    priority: z.enum(['high', 'medium', 'low']).describe(
                        `The priority level of acting on this conversation.

- High priority is reserved strictly for conversations that are extremely urgent or business-critical. Only use high priority if immediate action is required (e.g., a time-sensitive opportunity, a critical ongoing negotiation, or a situation where a delay could result in significant loss or missed opportunity). High priority should be rare and only assigned to the most important cases.
- Medium priority should be used for conversations that are relevant and valuable to the user request, but are not urgent or business-critical. These are conversations where action is recommended, but it is not time-sensitive or critical. If there is no ongoing conversation with the contact, this is the highest priority that conversation should be.
- Low priority should be used for conversations that are not directly relevant to the user request or are informational only. The user will probably not act on this conversation, but it might match the user request and is worth seeing.

The priority level will be displayed to the user in the UI.`,
                    ),
                    reason: z
                        .string()
                        .describe(
                            'The reason why this contact/action is being displayed and matters to the user, will be communicated to the user in the UI. The reason should be one sentence and answer how it relates to the users request.',
                        ),
                    message: z
                        .string()
                        .describe(
                            'The suggested message you think the user should send to the contact. The message is from the users perspective as they will be the one sending it.',
                        ),
                }),
            )
            .describe(
                'The conversations to display with their priority levels, reasons and suggested messages to send',
            ),
    }),
    outputSchema: z.string(),
    toolCallUi: (input) => null,
});
